import { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import { RegionalData } from '@/types/pricing-regional.types';
import { FuelType } from '@/types/price-comparison.types';

import {
  PricingFilters,
  useExportDetailedToExcelMutation,
  useExportRegionalToExcelMutation,
  useGetBrandFiltersQuery,
  useGetRegionalDataQuery,
  useGetSummaryQuery
} from '../../../services';
import { useRolePermissions, useSnackbar } from '../../../hooks';
import { useAppSelector } from '../../../store';

import { BrandOrderDialog, ErrorState, LoadingTable, PerformanceStatusIndicator } from '../../../components';

import { TableControls, SummaryTable, RegionalTable, RegionalTableHeader, PriceType } from '../components';

import './PricingRegionalPage.css';

function PricingRegionalPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { canExport, canCreate, canViewSales } = useRolePermissions();
  const isAuthorized = useAppSelector(state => state.global.isAuthorized);

  const [brandDialogOpen, setBrandDialogOpen] = useState(false);
  const [brandDialogRegionalOpen, setBrandDialogRegionalOpen] = useState(false);
  const [hideDiscounts, setHideDiscounts] = useState(false);
  const [collapsedRegions, setCollapsedRegions] = useState<Record<string, boolean>>({});
  const [priceTypes, setpriceTypes] = useState<PriceType>(PriceType.MODE);

  const { setSnackbarMessage } = useSnackbar();
  const filters: PricingFilters = {
    includeDiscounts: !hideDiscounts
  };

  const {
    data: brands,
    isLoading: brandsLoading,
    isSuccess: brandsSuccess,
    error: brandsError,
    refetch: refetchBrands
  } = useGetBrandFiltersQuery(undefined, { skip: !isAuthorized, refetchOnMountOrArgChange: true });

  const {
    data: { summaryData = [], summaryFuelTypes = [], performanceStatus } = {},
    isLoading: summaryLoading,
    isFetching: summaryFetching,
    isSuccess: summarySuccess,
    error: summaryError,
    refetch: refetchSummary
  } = useGetSummaryQuery(filters, {
    refetchOnMountOrArgChange: true,
    skip: !isAuthorized
  });

  const {
    data: { regionalData = [], regionalFuelTypes = [] } = {},
    isLoading: regionalLoading,
    isSuccess: regionalSuccess,
    error: regionalError,
    refetch: refetchRegional
  } = useGetRegionalDataQuery(undefined, {
    refetchOnMountOrArgChange: true,
    skip: !isAuthorized
  });

  const [exportDetailedToExcel] = useExportDetailedToExcelMutation();
  const [exportRegionalToExcel] = useExportRegionalToExcelMutation();

  const isAuthError = (err: any) => err?.status === 401 || err?.status === 403;
  const isError =
    (brandsError || summaryError || regionalError) &&
    !isAuthError(brandsError) &&
    !isAuthError(summaryError) &&
    !isAuthError(regionalError);
  const isLoading = brandsLoading || summaryLoading || regionalLoading;
  const isDataLoaded = brandsSuccess || summarySuccess || regionalSuccess;
  const availableBrands = brands?.brands || [];
  const apiFuelTypes = summaryFuelTypes.length > 0 ? summaryFuelTypes : regionalFuelTypes;
  const fuelTypes = apiFuelTypes.map((fuel: FuelType): string => fuel.fuel_type);
  const brandOrderLabel = isMobile ? 'Бренди (рег.)' : 'Налаштувати бренди (регіональні)';

  useEffect(() => {
    if (isError) {
      setSnackbarMessage('Помилка завантаження даних', 'error');
    }
  }, [isError, setSnackbarMessage]);

  const isRegionCollapsed = (region: string) => !!collapsedRegions[region];

  const toggleRegion = (region: string) => () => {
    setCollapsedRegions(prev => ({
      ...prev,
      [region]: !prev[region]
    }));
  };

  const handelePriceTypes = (event: ChangeEvent<HTMLInputElement>) => {
    setpriceTypes(event.target.value as PriceType);
  }

  const handleBrandDialogOpen = (state: boolean) => () => {
    if (!canCreate) return;
    setBrandDialogOpen(state);
  };

  const handleBrandDialogRegionalOpen = (state: boolean) => () => {
    if (!canCreate) return;
    setBrandDialogRegionalOpen(state);
  };

  const handleExportDetailed = () => {
    if (!canExport) return;
    exportPrices();
  };

  const handleExportRegional = (region: string) => () => {
    if (!canExport) return;
    exportRegionalPrices(region);
  };

  const handleViewDetails = (region: string) => () => navigate(`/pricing-regional/regional/${region}`);

  const exportPrices = async () => {
    await exportDetailedToExcel(!hideDiscounts)
      .unwrap()
      .then(() => setSnackbarMessage('Файл успішно вивантажено', 'success'))
      .catch(() => setSnackbarMessage('Помилка вивантаження файлу', 'error'));
  };

  const exportRegionalPrices = async (region: string) => {
    await exportRegionalToExcel({
      region,
      includeDiscounts: !hideDiscounts
    })
      .unwrap()
      .then(() => setSnackbarMessage(`Файл для регіону ${region} успішно вивантажено`, 'success'))
      .catch(() => setSnackbarMessage('Помилка вивантаження файлу', 'error'));
  };

  const refetchData = () => {
    refetchSummary();
    refetchRegional();
  };

  const retryLoad = () => {
    refetchBrands();
    refetchData();
  };

  const formatPrice = (price?: number | null): string =>
    typeof price === 'number' && !isNaN(price) ? price.toFixed(2) : '';

  if (isError) {
    return (
      <div className="pricing-container">
        <ErrorState message="Помилка завантаження цін" onRetry={retryLoad} />
      </div>
    );
  }

  return (
    <div className="pricing-container">
      {isDataLoaded && <PerformanceStatusIndicator performanceStatus={performanceStatus} onRefreshNeeded={retryLoad} />}
      {isLoading ? (
        <LoadingTable message="Завантаження цін..." rows={10} columns={15} />
      ) : (
        <>
          {/* Summary Table */}
          <div className="summary-table-container">
            <h2 className="table-title">Узагальнена таблиця</h2>

            <TableControls
              showExport={canExport}
              showBrandOrder={canCreate}
              showHideDiscounts={canExport}
              isMobile={isMobile}
              hideDiscounts={hideDiscounts}
              onChangePriceTypes={handelePriceTypes}
              priceTypes={priceTypes}
              onHideDiscountsChange={setHideDiscounts}
              onExport={handleExportDetailed}
              onBrandOrder={handleBrandDialogOpen(true)}
            />

            <SummaryTable
              summaryData={summaryData}
              fuelTypes={fuelTypes}
              priceType={priceTypes}
              hideDiscounts={hideDiscounts}
              formatPrice={formatPrice}
              isMobile={isMobile}
              summaryFetching={summaryFetching}
              hideSales={!canViewSales}
            />
          </div>

          {/* Regional Tables */}
          <div className="regional-tables-container">
            <TableControls
              title="Роздрібні ціни по регіонах"
              showExport={false}
              showBrandOrder={canCreate}
              showHideDiscounts={false}
              isMobile={isMobile}
              brandOrderLabel={brandOrderLabel}
              onBrandOrder={handleBrandDialogRegionalOpen(true)}
              // priceTypes={priceTypes}
              // onChangePriceTypes={handelePriceTypes}
            />

            {regionalData.map((regionData: RegionalData) => {
              const region = regionData.region;
              return (
                <div key={`region-${region}`} className="regional-table-container">
                  <RegionalTableHeader
                    title={region}
                    isCollapsed={isRegionCollapsed(region)}
                    onToggle={toggleRegion(region)}
                    onExport={handleExportRegional(region)}
                    onViewDetails={handleViewDetails(region)}
                    showExport={canExport}
                    showViewDetails={canExport}
                    isMobile={isMobile}
                  />
                  {!isRegionCollapsed(region) && (
                    <RegionalTable
                      brands={regionData.brands}
                      fuelTypes={fuelTypes}
                      priceType={priceTypes}
                      formatPrice={formatPrice}
                      isMobile={isMobile}
                      regionKey={region}
                      lastAuthor={regionData.lastAuthor}
                      lastUploadDatetime={regionData.lastUploadDatetime}
                      hideSales={!canViewSales}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <BrandOrderDialog
        scope="general"
        context="region"
        open={brandDialogOpen}
        availableBrands={availableBrands}
        onClose={handleBrandDialogOpen(false)}
      />
      <BrandOrderDialog
        scope="regional"
        context="region"
        open={brandDialogRegionalOpen}
        availableBrands={availableBrands}
        onClose={handleBrandDialogRegionalOpen(false)}
      />
    </div>
  );
}

export default PricingRegionalPage;
